<template>
  <div class="resignation-management">
    <div class="main-content">
      <div class="filter-section">
        <div class="filter-form">
          <div class="filter-item">
            <a-input
              v-model:value="filters.searchText"
              placeholder="搜索姓名 / 身份证号..."
              allowClear
              @pressEnter="handleSearch"
              @change="handleSearchChange"
            />
          </div>
          <div class="filter-item">
            <a-select
              v-model:value="filters.status"
              placeholder="合同状态"
              allowClear
              style="width: 100%;"
              @change="handleFilterChange"
            >
              <a-select-option value="">全部状态</a-select-option>
              <a-select-option value="pending">待发起</a-select-option>
              <a-select-option value="signing">员工签署中</a-select-option>
              <a-select-option value="signed">签署完成</a-select-option>
              <a-select-option value="expired">已过期</a-select-option>
              <a-select-option value="rejected">拒绝签署</a-select-option>
            </a-select>
          </div>
        </div>
        <div class="filter-actions">
          <a-button type="primary" @click="handleSearch" style="border-radius: 4px;">查询</a-button>
          <a-button @click="handleReset" style="border-radius: 4px;">重置</a-button>
        </div>
      </div>

      <div class="table-section">
        <div class="table-header">
          <div class="table-header-left">
            <div class="stat-item">
              <span class="stat-label">离职申请总数</span>
              <span class="stat-value primary">{{ statistics.total }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">待发起</span>
              <span class="stat-value warning">{{ statistics.pending }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">员工签署中</span>
              <span class="stat-value processing">{{ statistics.signing }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">签署完成</span>
              <span class="stat-value success">{{ statistics.signed }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">已过期</span>
              <span class="stat-value expired">{{ statistics.expired }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">拒绝签署</span>
              <span class="stat-value danger">{{ statistics.rejected }}</span>
            </div>
          </div>
        </div>
        <vxe-table
          ref="vxeTableRef"
          :data="filteredData"
          auto-resize
          :column-config="{ resizable: true }"
          :row-config="{ isHover: true, height: 40 }"
          :show-overflow="'title'"
        >
          <vxe-column type="seq" width="60" title="序号" fixed="left" />
          <vxe-column field="name" title="姓名" min-width="100" fixed="left" />
          <vxe-column field="phone" title="手机号" min-width="140" />
          <vxe-column field="idCard" title="身份证号" min-width="180">
            <template #default="{ row }">
              <span>{{ row.idCard }}</span>
            </template>
          </vxe-column>
          <vxe-column field="contractStartDate" title="合同开始日期" min-width="130" />
          <vxe-column field="status" title="合同状态" min-width="120">
            <template #default="{ row }">
              <span v-if="row.status === 'pending'" style="display: flex; align-items: center; gap: 4px;">
                <a-badge status="warning" />待发起
              </span>
              <span v-else-if="row.status === 'signing'" style="display: flex; align-items: center; gap: 4px;">
                <a-badge status="processing" />员工签署中
              </span>
              <span v-else-if="row.status === 'signed'" style="display: flex; align-items: center; gap: 4px;">
                <a-badge status="success" />签署完成
              </span>
              <span v-else-if="row.status === 'expired'" style="display: flex; align-items: center; gap: 4px;">
                <a-badge status="error" />已过期
              </span>
              <span v-else-if="row.status === 'rejected'" style="display: flex; align-items: center; gap: 4px;">
                <a-badge status="error" />拒绝签署
              </span>
            </template>
          </vxe-column>
          <vxe-column field="contractBody" title="合同主体" min-width="180" />
          <vxe-column title="操作" width="120" fixed="right" align="right">
            <template #default="{ row }">
              <template v-if="row.status === 'pending'">
                <button class="link-button" @click="handleResign(row)">重新发起</button>
                <button class="link-button" @click="handleView(row)">查看</button>
              </template>
              <template v-else-if="row.status === 'signing'">
                <button class="link-button" @click="handleRemind(row)">催签</button>
                <button class="link-button" @click="handleView(row)">查看</button>
              </template>
              <template v-else-if="row.status === 'expired' || row.status === 'rejected'">
                <button class="link-button" @click="handleResign(row)">重签</button>
                <button class="link-button" @click="handleView(row)">查看</button>
              </template>
              <template v-else>
                <button class="link-button" @click="handleView(row)">查看</button>
              </template>
            </template>
          </vxe-column>
        </vxe-table>

        <div class="pagination-wrapper">
          <a-pagination
            v-model:current="currentPage"
            :page-size="pageSize"
            :page-size-options="['10', '20', '50', '100']"
            :total="filteredData.length"
            :showSizeChanger="true"
            :showQuickJumper="true"
            :showTotal="showTotalText"
            @change="handlePageChange"
            @showSizeChange="handleSizeChange"
          />
        </div>
      </div>
    </div>

    <Drawer :visible="viewModalVisible" @update:visible="viewModalVisible = $event">
      <template #title><span class="drawer-title-text">员工合同预览</span><span class="drawer-title-name" v-if="viewData?.name">（{{ viewData.name }}）</span></template>
      <div v-if="viewData" class="esign-preview-wrapper">
        <div class="esign-document-preview">
          <div class="section-title">合同文档</div>
          <div class="document-container">
            <div class="document-page">
              <div class="document-content">
                <div class="doc-header">解除劳动合同协议书</div>
                <div class="doc-body">
                  <p>甲方（用人单位）：{{ viewData.contractBody || '深圳外包服务有限公司' }}</p>
                  <p>乙方（劳动者）：{{ viewData.name }}</p>
                  <p>身份证号码：{{ viewData.idCard }}</p>
                  <p>&nbsp;</p>
                  <p>根据《中华人民共和国劳动合同法》及相关法律法规的规定，甲乙双方经协商一致，就解除劳动合同事宜达成如下协议：</p>
                  <p>&nbsp;</p>
                  <p>一、甲乙双方同意于{{ viewData.contractStartDate || '________' }}解除劳动合同。</p>
                  <p>&nbsp;</p>
                  <p>二、甲方应向乙方支付经济补偿金共计人民币___________元。</p>
                  <p>&nbsp;</p>
                  <p>三、乙方应在离职前完成工作交接，并交还公司财物。</p>
                  <p>&nbsp;</p>
                  <p>四、本协议一式两份，甲乙双方各执一份，具有同等法律效力。</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Drawer>

    <a-modal
      v-model:visible="contractModalVisible"
      title="发起解除劳动合同协议"
      @ok="handleContractSubmit"
      :okText="contractSubmitLoading ? '正在发起中...' : '确认发起'"
      cancelText="取消"
      width="600px"
    >
      <a-form :form="contractForm" layout="vertical">
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item label="员工姓名">
              <a-input v-decorator="['name']" readOnly style="background: #F7F8FA;" />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="身份证号">
              <a-input v-decorator="['idCard']" readOnly style="background: #F7F8FA;" />
            </a-form-item>
          </a-col>
        </a-row>
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item label="客户名称">
              <a-input v-decorator="['customer']" readOnly style="background: #F7F8FA;" />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="工作中心">
              <a-input v-decorator="['workCenter']" readOnly style="background: #F7F8FA;" />
            </a-form-item>
          </a-col>
        </a-row>
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item label="合同主体">
              <a-select
                v-decorator="['contractBody', { initialValue: '' }]"
                placeholder="请选择合同主体"
              >
                <a-select-option value="深圳外包服务有限公司">深圳外包服务有限公司</a-select-option>
                <a-select-option value="北京人力资源服务公司">北京人力资源服务公司</a-select-option>
                <a-select-option value="上海人才服务有限公司">上海人才服务有限公司</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="合同开始日期" required>
              <a-date-picker
                v-decorator="['contractStartDate', { rules: [{ required: true, message: '请选择合同开始日期' }] }]"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
        </a-row>
        <a-form-item label="合同模板" required>
          <a-select
            v-decorator="['contractTemplate', { rules: [{ required: true, message: '请选择合同模板' }] }]"
            placeholder="请选择合同模板"
          >
            <a-select-option value="standard">解除劳动合同协议-标准版</a-select-option>
            <a-select-option value="negotiate">解除劳动合同协议-协商版</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="备注">
          <a-textarea v-decorator="['remark']" :rows="3" placeholder="可选填写备注信息..." />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import moment from 'moment'
import resignationDataSource from '@/data/resignationSource'
import Drawer from '../components/Drawer.vue'

export default {
  name: 'ResignationManagement',
  components: {
    Drawer
  },
  data() {
    return {
      filters: {
        searchText: '',
        status: undefined,
        customer: undefined,
        workCenter: undefined
      },
      currentPage: 1,
      pageSize: 10,
      currentRecordId: null,
      viewModalVisible: false,
      contractModalVisible: false,
      contractSubmitLoading: false,
      viewData: null,
      esignContractData: {
        contractNo: '',
        createTime: '',
        companySignTime: '',
        employeeSignTime: ''
      },
      contractForm: this.$form.createForm(this),
      tableData: []
    }
  },
  computed: {
    filteredData() {
      let data = [...this.tableData]
      
      if (this.filters.searchText) {
        const search = this.filters.searchText.toLowerCase()
        data = data.filter(item => 
          item.name.toLowerCase().includes(search) || 
          item.idCard.includes(search)
        )
      }
      
      if (this.filters.status) {
        data = data.filter(item => item.status === this.filters.status)
      }
      
      return data.sort((a, b) => {
        const timeA = a.createTime || '1970-01-01 00:00:00'
        const timeB = b.createTime || '1970-01-01 00:00:00'
        return timeB.localeCompare(timeA)
      })
    },
    statistics() {
      const total = this.tableData.length
      const pending = this.tableData.filter(item => item.status === 'pending').length
      const signing = this.tableData.filter(item => item.status === 'signing').length
      const signed = this.tableData.filter(item => item.status === 'signed').length
      const expired = this.tableData.filter(item => item.status === 'expired').length
      const rejected = this.tableData.filter(item => item.status === 'rejected').length
      return { total, pending, signing, signed, expired, rejected }
    }
  },
  mounted() {
    resignationDataSource.init((data) => {
      this.tableData = data
    })

    this.receiveDataFromThirdParty()
  },
  methods: {
    formatIdCard(idCard) {
      if (!idCard) return '-'
      return idCard
    },
    getTimelineColor(color) {
      const colorMap = {
        'blue': '#516EFD',
        'green': '#0DD78D',
        'orange': '#FEAE4D',
        'red': '#FD5451'
      }
      return colorMap[color] || '#516EFD'
    },
    getStatusText(status) {
      const statusMap = {
        'pending': '待发起',
        'signing': '员工签署中',
        'signed': '签署完成',
        'archived': '已归档',
        'expired': '已过期',
        'rejected': '员工拒绝签署'
      }
      return statusMap[status] || status
    },
    showTotalText(total) {
      return `共 ${total} 条`
    },
    handleSearch() {
      this.currentPage = 1
    },
    handleSearchChange() {
      this.handleSearch()
    },
    handleFilterChange() {
      this.currentPage = 1
    },
    handleReset() {
      this.filters = {
        searchText: '',
        status: undefined
      }
      this.currentPage = 1
    },
    handleSizeChange(current, size) {
      this.pageSize = size
      this.currentPage = 1
    },
    handlePageChange(page) {
      this.currentPage = page
    },
    handleCreate() {
      this.contractForm.resetFields()
      this.contractModalVisible = true
    },
    handleView(record) {
      this.viewData = record
      this.esignContractData = {
        contractNo: `ESIGN-${record.id || Date.now()}-2026`,
        createTime: record.createTime || new Date().toLocaleString('zh-CN'),
        companySignTime: record.status === 'signed' ? this.getRandomSignTime() : '',
        employeeSignTime: record.status === 'signed' ? this.getRandomSignTime() : ''
      }
      this.viewModalVisible = true
    },
    getRandomSignTime() {
      const date = new Date()
      date.setDate(date.getDate() - Math.floor(Math.random() * 10))
      return date.toLocaleString('zh-CN')
    },
    handleContract(record) {
      this.currentRecordId = record.id
      this.contractModalVisible = true
      setTimeout(() => {
        this.contractForm.setFieldsValue({
          name: record.name,
          idCard: record.idCard,
          customer: record.customer,
          workCenter: record.workCenter,
          contractBody: record.contractBody,
          contractStartDate: record.contractStartDate ? moment(record.contractStartDate, 'YYYY-MM-DD') : null,
          contractTemplate: record.contractTemplate,
          remark: record.remark
        })
      }, 100)
    },
    handleContractSubmit() {
      this.contractSubmitLoading = true
      this.contractForm.validateFields((err, values) => {
        if (!err) {
          setTimeout(() => {
            const recordId = this.viewData?.id || this.currentRecordId
            if (recordId) {
              const record = resignationDataSource.getItem(recordId)
              if (record) {
                resignationDataSource.updateItem(recordId, {
                  contractBody: values.contractBody,
                  contractStartDate: values.contractStartDate ? values.contractStartDate.format('YYYY-MM-DD') : values.contractStartDate,
                  contractTemplate: values.contractTemplate,
                  remark: values.remark
                })
                resignationDataSource.updateStatus(recordId, 'signing', {
                  title: '发起解除劳动合同协议',
                  desc: `合同主体：${values.contractBody || record.contractBody || '-'}，合同模板：${values.contractTemplate || '-'}`,
                  color: 'blue'
                })
              }
            }
            this.$message.success('合同发起成功')
            this.pushToThirdParty()
            this.contractModalVisible = false
            this.contractForm.resetFields()
            this.contractSubmitLoading = false
          }, 1000)
        } else {
          this.contractSubmitLoading = false
        }
      })
    },
    handleRemind(record) {
      this.$confirm({
        title: '确认催签',
        content: `确定要向 ${record.name} 发送催签提醒吗？`,
        okText: '确定',
        cancelText: '取消',
        icon: 'exclamation-circle',
        onOk: () => {
          resignationDataSource.updateStatus(record.id, record.status, {
            title: '合同催签',
            desc: '系统自动发送催签短信提醒',
            color: 'orange'
          })
          this.$message.success('已发送催签提醒')
          this.pushToThirdParty()
        }
      })
    },
    handleResign(record) {
      const isPending = record.status === 'pending'
      const title = isPending ? '确认重新发起' : '确认重签'
      const content = isPending
        ? `确定要为员工 ${record.name} 重新发起合同签署吗？`
        : `确定要为员工 ${record.name} 重新发起合同签署吗？`

      this.$confirm({
        title,
        content,
        okText: '确定',
        cancelText: '取消',
        icon: 'exclamation-circle',
        onOk: () => {
          resignationDataSource.updateStatus(record.id, 'signing', {
            title: isPending ? '重新发起合同' : '合同重签',
            desc: isPending ? '重新发起合同签署流程' : '重新发起合同签署流程',
            color: 'blue'
          })
          this.$message.success(isPending ? '重新发起成功' : '重签成功')
          this.pushToThirdParty()
        }
      })
    },
    receiveDataFromThirdParty() {
      const thirdPartyData = [
        {
          id: 1,
          name: '张三',
          phone: '13800138001',
          idCard: '320123199001011234',
          contractStartDate: '2026-05-15',
          contractBody: '深圳外包服务有限公司',
          status: 'signed',
          createTime: '2026-05-15 09:00:00'
        },
        {
          id: 2,
          name: '李四',
          phone: '13800138002',
          idCard: '440123199002022345',
          contractStartDate: '2026-05-18',
          contractBody: '北京人力资源服务公司',
          status: 'signing',
          createTime: '2026-05-18 09:00:00'
        },
        {
          id: 3,
          name: '王五',
          phone: '13800138003',
          idCard: '110123199003033456',
          contractStartDate: '2026-04-20',
          contractBody: '上海人才服务有限公司',
          status: 'expired',
          createTime: '2026-04-20 09:00:00'
        },
        {
          id: 4,
          name: '赵六',
          phone: '13800138004',
          idCard: '330123199004044567',
          contractStartDate: '2026-05-22',
          contractBody: '深圳外包服务有限公司',
          status: 'pending',
          createTime: '2026-05-22 09:00:00'
        },
        {
          id: 5,
          name: '孙七',
          phone: '13800138005',
          idCard: '510123199005055678',
          contractStartDate: '2026-05-10',
          contractBody: '深圳外包服务有限公司',
          status: 'rejected',
          createTime: '2026-05-10 09:00:00'
        },
        {
          id: 6,
          name: '周八',
          phone: '13800138006',
          idCard: '420123199006066789',
          contractStartDate: '2026-05-25',
          contractBody: '北京人力资源服务公司',
          status: 'signing',
          createTime: '2026-05-25 09:00:00'
        },
        {
          id: 7,
          name: '吴九',
          phone: '13800138007',
          idCard: '610123199007077890',
          contractStartDate: '2026-05-08',
          contractBody: '上海人才服务有限公司',
          status: 'signed',
          createTime: '2026-05-08 09:00:00'
        },
        {
          id: 8,
          name: '郑十',
          phone: '13800138008',
          idCard: '370123199008088901',
          contractStartDate: '2026-05-28',
          contractBody: '深圳外包服务有限公司',
          status: 'pending',
          createTime: '2026-05-28 10:00:00'
        },
        {
          id: 9,
          name: '钱十一',
          phone: '13800138009',
          idCard: '210123199009099012',
          contractStartDate: '2026-04-15',
          contractBody: '北京人力资源服务公司',
          status: 'signed',
          createTime: '2026-04-15 09:00:00'
        },
        {
          id: 10,
          name: '孙十二',
          phone: '13800138010',
          idCard: '130123199010101123',
          contractStartDate: '2026-05-01',
          contractBody: '上海人才服务有限公司',
          status: 'rejected',
          createTime: '2026-05-01 14:00:00'
        }
      ]
      resignationDataSource.setData(thirdPartyData)
    },
    pushToThirdParty() {
      const result = resignationDataSource.pushToThirdParty()
      console.log('推送数据给第三方:', result)
    }
  }
}
</script>

<style scoped>
.resignation-management {
  padding: 24px;
  background: #FFFFFF;
  overflow-x: auto;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  min-height: 0;
}

:deep(.ant-select) .ant-select-selector {
  border-color: #D7D7DB !important;
  transition: all 0.3s ease !important;
}

:deep(.ant-select:not(.ant-select-disabled):hover) .ant-select-selector {
  border-color: #FC6732 !important !important;
}

:deep(.ant-select-focused) .ant-select-selector,
:deep(.ant-select-selector:focus),
:deep(.ant-select-selector:active) {
  border-color: #FC6732 !important !important;
  box-shadow: 0 0 0 2px rgba(252, 103, 50, 0.2) !important !important;
}

:deep(.ant-select-open) .ant-select-selector {
  border-color: #FC6732 !important !important;
}

:deep(.ant-select-dropdown-menu-item:hover) {
  background-color: #F7F8FA !important;
  color: #1B1C33 !important;
}

:deep(.ant-select-dropdown-menu-item-selected),
:deep(.ant-select-dropdown-menu-item-selected:hover) {
  background-color: #F7F8FA !important;
  color: #FC6732 !important !important;
}

:deep(.ant-select-dropdown-menu-item-active),
:deep(.ant-select-dropdown-menu-item-active:hover) {
  background-color: #F7F8FA !important;
  color: #FC6732 !important !important;
}

.main-content {
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.filter-section {
  display: flex;
  align-items: center;
  column-gap: 16px;
  border: 1px solid #D7D7DB;
  border-radius: 8px;
  background: #F7F8FA;
  padding: 23px;
  flex-wrap: wrap;
  margin-bottom: 24px;
  flex-shrink: 0;
}

.filter-form {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  flex: 1;
}

.filter-item {
  min-width: 0;
  display: block;
  width: 100%;
}

.filter-item :deep(.ant-select),
.filter-item :deep(.ant-input),
.filter-item :deep(.ant-cascader) {
  width: 100% !important;
  display: block !important;
}

.filter-actions {
  display: inline-flex;
  align-items: flex-start;
  column-gap: 16px;
  box-shadow: inset 1px 0px 0px 0px #D7D7DB;
  background: #F6F7F8;
  padding-left: 16px;
}

.filter-actions :deep(.ant-btn) {
  height: 32px;
  border-radius: 4px;
  border-color: #D7D7DB;
  color: #1B1C33;
}

.filter-actions :deep(.ant-btn:hover) {
  color: #FC6732;
  border-color: #FC6732;
}

.filter-actions :deep(.ant-btn-primary) {
  background-color: #FC6732;
  border-color: #FC6732;
  color: #FFFFFF;
}

.filter-actions :deep(.ant-btn-primary:hover) {
  background-color: #FC7647;
  border-color: #FC7647;
  color: #FFFFFF;
}

.table-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 8px;
  overflow: visible;
  min-height: 0;
  margin-bottom: 24px;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 0 16px 0;
  border-bottom: 1px solid #E8E8E8;
}

.table-header-left {
  display: flex;
  gap: 24px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.stat-label {
  font-size: 13px;
  color: #6B7280;
}

.stat-value {
  font-size: 16px;
  font-weight: 600;
  color: #1B1C33;
}

.stat-value.primary {
  color: #516EFD;
}

.stat-value.warning {
  color: #FEAE4D;
}

.stat-value.success {
  color: #0DD78A;
}

.stat-value.processing {
  color: #516EFD;
}

.stat-value.error,
.stat-value.expired,
.stat-value.danger {
  color: #FD5451;
}

.table-header-right {
  display: flex;
}

.table-header-right :deep(.ant-btn) {
  height: 32px;
  border-radius: 4px;
  border-color: #D7D7DB;
  color: #1B1C33;
}

.table-header-right :deep(.ant-btn:hover) {
  color: #FC6732;
  border-color: #FC6732;
}

.table-header-right :deep(.ant-btn-primary) {
  background-color: #FC6732;
  border-color: #FC6732;
  color: #FFFFFF;
}

.table-header-right :deep(.ant-btn-primary:hover) {
  background-color: #FC7647;
  border-color: #FC7647;
  color: #FFFFFF;
}

:deep(.ant-table) {
  background: #fff;
}

:deep(.ant-table-thead > tr > th) {
  background: #F7F8FA;
  color: #1B1C33;
  font-weight: 600;
  font-size: 13px;
  border-bottom: 1px solid #D7D7DB;
  padding: 12px 16px;
}

:deep(.ant-table-tbody > tr > td) {
  padding: 12px 16px;
  border-bottom: 1px solid #E8E8E8;
  font-size: 14px;
  color: #1B1C33;
}

:deep(.ant-table-tbody > tr:hover > td) {
  background: #FFF9F7;
}

:deep(.ant-table-tbody > tr:last-child > td) {
  border-bottom: none;
}

.link-button {
  background: none;
  border: none;
  padding: 0;
  color: #516EFD;
  cursor: pointer;
  font-size: 13px;
  margin-right: 8px;
}

.link-button:hover {
  color: #FC6732;
  text-decoration: underline;
}

.link-button.warning {
  color: #FEAE4D;
}

.link-button.danger {
  color: #FD5451;
}

.pagination-wrapper {
  display: flex;
  justify-content: flex-end;
  padding: 16px 0;
}

.pagination-wrapper :deep(.ant-pagination-item-active) {
  border-color: #FC6732;
}

.pagination-wrapper :deep(.ant-pagination-item-active a) {
  color: #FC6732;
}

.pagination-wrapper :deep(.ant-pagination-item:hover) {
  border-color: #FC6732;
}

.pagination-wrapper :deep(.ant-pagination-item:hover a) {
  color: #FC6732;
}

.pagination-wrapper :deep(.ant-pagination-prev:hover .ant-pagination-item-link),
.pagination-wrapper :deep(.ant-pagination-next:hover .ant-pagination-item-link) {
  border-color: #FC6732;
  color: #FC6732;
}

.pagination-wrapper :deep(.ant-pagination-options .ant-select-selection-item) {
  color: #FC6732;
}

.ant-modal-footer .ant-btn-primary {
  background: #FC6732 !important;
  border-color: #FC6732 !important;
}

.ant-modal-footer .ant-btn-primary:hover {
  background: #e55a2a !important;
  border-color: #e55a2a !important;
}

.detail-section {
  padding: 8px 0;
}

.detail-title {
  font-size: 15px;
  font-weight: 600;
  color: #1B1C33;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #E8E8E8;
}

.drawer-content {
  width: 100%;
}

.form-wrapper {
  width: 100%;
}

.form-title {
  font-size: 16px;
  font-weight: 600;
  color: #1B1C33;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.form-card {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  padding: 24px;
  box-sizing: border-box;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
  width: 100%;
  box-sizing: border-box;
  align-items: start;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-label {
  font-size: 14px;
  color: #8D8E99;
}

.detail-value {
  font-size: 14px;
  color: #1B1C33;
}

.timeline-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.timeline-item {
  display: flex;
  gap: 12px;
  padding: 12px 0;
  border-left: 2px solid #EDEDF0;
  margin-left: 6px;
  padding-left: 16px;
  position: relative;
}

.timeline-dot {
  position: absolute;
  left: -6px;
  top: 16px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.timeline-content {
  flex: 1;
  padding-bottom: 8px;
}

.timeline-title {
  font-size: 14px;
  font-weight: 600;
  color: #1B1C33;
}

.timeline-time {
  font-size: 12px;
  color: #9CA3AF;
  margin-top: 2px;
}

.timeline-desc {
  font-size: 13px;
  color: #6B7280;
  margin-top: 4px;
}

:deep(.ant-modal .ant-btn) {
  height: 32px;
  border-radius: 4px;
  border-color: #D7D7DB;
  color: #1B1C33;
}

:deep(.ant-modal .ant-btn:hover) {
  color: #FC6732;
  border-color: #FC6732;
}

:deep(.ant-modal .ant-btn-primary) {
  background-color: #FC6732;
  border-color: #FC6732;
  color: #FFFFFF;
}

:deep(.ant-modal .ant-btn-primary):hover {
  background-color: #FC7647;
  border-color: #FC7647;
  color: #FFFFFF;
}

.drawer-title-text {
  font-weight: 600;
  color: #1B1C33;
}

.drawer-title-name {
  font-weight: 600;
  color: #516EFD;
  margin: 0;
  padding: 0;
}

.esign-preview-wrapper {
  width: 100%;
}

.esign-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 20px;
  border-bottom: 1px solid #E8E8E8;
  margin-bottom: 20px;
}

.esign-logo {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #516EFD 0%, #3451FF 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
}

.logo-text {
  color: #FFFFFF;
  font-size: 14px;
  font-weight: 600;
}

.esign-title {
  font-size: 18px;
  font-weight: 600;
  color: #1B1C33;
}

.esign-contract-info {
  background: #F7F8FA;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
}

.contract-info-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.contract-info-row:last-child {
  margin-bottom: 0;
}

.info-label {
  font-size: 14px;
  color: #6B7280;
  width: 80px;
}

.info-value {
  font-size: 14px;
  color: #1B1C33;
}

.esign-employee-info {
  margin-bottom: 20px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #1B1C33;
  margin-bottom: 12px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.item-label {
  font-size: 13px;
  color: #8D8E99;
}

.item-value {
  font-size: 14px;
  color: #1B1C33;
}

.esign-document-preview {
  margin-bottom: 20px;
}

.document-container {
  border: 1px solid #E8E8E8;
  border-radius: 8px;
  overflow: hidden;
}

.document-page {
  background: #FFFFFF;
  padding: 32px;
  min-height: 400px;
}

.document-content {
  max-width: 600px;
  margin: 0 auto;
}

.doc-header {
  font-size: 20px;
  font-weight: 600;
  color: #1B1C33;
  text-align: center;
  margin-bottom: 24px;
}

.doc-body {
  font-size: 14px;
  color: #1B1C33;
  line-height: 1.8;
}

.doc-body p {
  margin: 0;
  text-indent: 2em;
}

.esign-signatures {
  margin-bottom: 20px;
}

.signature-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.signature-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #F7F8FA;
  border-radius: 8px;
}

.sig-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.company-sig {
  background: #E8F0FF;
}

.employee-sig {
  background: #E8FFF0;
}

.sig-info {
  flex: 1;
}

.sig-name {
  font-size: 14px;
  font-weight: 500;
  color: #1B1C33;
}

.sig-time {
  font-size: 12px;
  color: #8D8E99;
  margin-top: 2px;
}
</style>
